import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { normalizeDepartment } from '../utils/departmentMapping';
import {
  FiHome,
  FiMessageSquare,
  FiBarChart2,
  FiClipboard,
  FiSettings,
  FiBell,
  FiLogOut,
  FiMenu,
  FiX,
  FiUsers,
  FiClock,
  FiCalendar,
  FiUserPlus,
  FiLayers,
  FiChevronDown,
  FiZap,
  FiGrid,
  FiUserCheck,
  FiNavigation,
  FiSend,
  FiChevronsLeft,
  FiChevronsRight,
  FiMoreHorizontal,
  FiAlertCircle
} from 'react-icons/fi';
import brandLogoImg from '../assets/logo.png';
import faviconImg from '../assets/favicon.png';

const SIDEBAR_PRIORITY = [
  '/dashboard',
  '/chat',
  '/guests',
  '/rooms',
  '/requests',
  '/bookings',
  '/slots',
  '/analytics',
  '/settings'
];

function Layout({ children }) {
  const faviconSrc = faviconImg || (process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/favicon.png` : '/favicon.png');
  const brandLogoSrc = brandLogoImg || (process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/logo.png` : '/logo.png');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('operations');
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem('elychat.sidebarCollapsed') === '1';
    } catch {
      return false;
    }
  });
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const profileDropdownRef = useRef(null);
  const mobileProfileDropdownRef = useRef(null);
  const mainScrollRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const clickedInsideDesktop = profileDropdownRef.current && profileDropdownRef.current.contains(e.target);
      const clickedInsideMobile = mobileProfileDropdownRef.current && mobileProfileDropdownRef.current.contains(e.target);
      if (!clickedInsideDesktop && !clickedInsideMobile) {
        setProfileDropdownOpen(false);
      }
    };
    if (profileDropdownOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileDropdownOpen]);

  useEffect(() => {
    const el = mainScrollRef.current;
    if (!el) return;
    const onScroll = () => setScrolled(el.scrollTop > 12);
    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem('elychat.sidebarCollapsed', isCollapsed ? '1' : '0');
    } catch {}
  }, [isCollapsed]);

  // Close sidebar when navigating
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    setProfileDropdownOpen(false);
    logout();
    navigate('/login');
  };

  const getSettingsPath = () => '/settings';
  const hasSecurityPortalAccess = Array.isArray(user?.allowed_tabs) && user.allowed_tabs.includes('/security/login-history');

  const canAccessSettings = useMemo(() => {
    const allowedTabs = Array.isArray(user?.allowed_tabs) ? user.allowed_tabs : [];
    if (allowedTabs.length === 0) {
      return ['admin', 'manager', 'operator', 'department'].includes(user?.role);
    }
    return allowedTabs.includes('/settings');
  }, [user]);

  const getMenuItems = () => {
    const dashboardLabel =
      user?.role === 'admin' || user?.role === 'manager'
        ? 'Admin Dashboard'
        : user?.role === 'department' && user?.department
        ? `${user.department} Dashboard`
        : 'Dashboard';

    const allItems = [
      { path: '/dashboard', icon: FiHome, label: dashboardLabel, roles: ['admin', 'manager', 'operator', 'department'] },
      { path: '/chat', icon: FiMessageSquare, label: 'Live Chat', roles: ['admin', 'manager', 'operator'] },
      { path: '/ai-chat', icon: FiZap, label: 'AI Assistant', roles: ['admin', 'manager'] },
      { path: '/agent-pilot', icon: FiZap, label: 'Agent Pilot', roles: ['admin', 'manager'], hidden: true },
      { path: '/guests', icon: FiUsers, label: 'Guests', roles: ['admin', 'manager', 'department'] },
      { path: '/rooms', icon: FiLayers, label: 'Rooms', roles: ['admin', 'manager'] },
      { path: '/analytics', icon: FiBarChart2, label: 'Analytics', roles: ['admin', 'manager'] },
      { path: '/feedback', icon: FiMessageSquare, label: 'Feedback & Reviews', roles: ['admin', 'manager'] },
      { path: '/requests', icon: FiClipboard, label: 'Service Requests', roles: ['admin', 'manager', 'department'] },
      { path: '/critical-fixes', icon: FiAlertCircle, label: 'Critical Fixes', roles: ['admin', 'manager', 'department'] },
      {
        path: '/bookings',
        icon: FiCalendar,
        label: 'Activity & Bookings',
        roles: ['admin', 'manager', 'department'],
        departments: ['Yoga', 'Yoga & Wellness', 'SPA Appointment', 'Salon']
      },
      {
        path: '/slots',
        icon: FiClock,
        label: 'Slot Management',
        roles: ['admin', 'manager', 'department'],
        departments: ['Yoga', 'Yoga & Wellness', 'SPA Appointment', 'Salon']
      },
      { path: '/reminders', icon: FiBell, label: 'Reminders', roles: ['admin', 'manager', 'department', 'operator'] },
      { path: '/qr', icon: FiGrid, label: 'QR Rooms', roles: ['admin', 'manager'] },
      { path: '/attendance', icon: FiUserCheck, label: 'Attendance', roles: ['admin', 'manager'], hidden: true },
      { path: '/guest-memory', icon: FiUsers, label: 'Guest Memory', roles: ['admin', 'manager'], hidden: true },
      { path: '/users', icon: FiUserPlus, label: 'Users', roles: ['admin', 'manager'], security_only: true },
      { path: '/security/login-history', icon: FiNavigation, label: 'Login Activity', roles: ['admin', 'manager'], security_only: true },
      { path: '/department-staff', icon: FiUsers, label: 'Department Staff', roles: ['admin', 'manager', 'department'] },
      { path: '/settings', icon: FiSettings, label: 'Settings', roles: ['admin', 'manager', 'operator', 'department'] },
      { path: '/whitelabel', icon: FiSettings, label: 'White Label', roles: ['admin'] }
    ];

    const applySecurityFilter = (items) => items.filter((item) => !item.security_only || hasSecurityPortalAccess);

    const applyAllowedTabsFilter = (items) => {
      const allowedTabs = Array.isArray(user?.allowed_tabs) ? user.allowed_tabs : [];
      if (allowedTabs.length === 0) return items;
      const allowedSet = new Set(allowedTabs);
      return items.filter((item) => item.path === '/dashboard' || allowedSet.has(item.path) || (item.security_only && hasSecurityPortalAccess));
    };

    if (user?.role === 'admin' || user?.role === 'manager') {
      return applyAllowedTabsFilter(applySecurityFilter(allItems.filter((item) => !item.hidden)));
    }

    if (user?.role === 'department') {
      const departmentItems = allItems.filter((item) => {
        if (item.hidden) return false;
        if (!item.roles.includes('department')) return false;
        if (item.departments && user.department) {
          const userDept = normalizeDepartment(user.department || '').trim().toLowerCase();
          return item.departments.some((d) => normalizeDepartment(d || '').trim().toLowerCase() === userDept);
        }
        return true;
      });
      return applyAllowedTabsFilter(applySecurityFilter(departmentItems));
    }

    return applyAllowedTabsFilter(applySecurityFilter(allItems.filter((item) => !item.hidden && item.roles.includes('operator'))));
  };

  const menuItems = getMenuItems();
  const isChatPage = location.pathname === '/chat';
  const firstName = user?.name?.split(' ')[0] || 'User';
  const initials =
    user?.name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';

  const sidebarItems = useMemo(() => {
    const rank = new Map(SIDEBAR_PRIORITY.map((path, index) => [path, index]));
    return [...menuItems].sort((a, b) => {
      const aRank = rank.has(a.path) ? rank.get(a.path) : 999;
      const bRank = rank.has(b.path) ? rank.get(b.path) : 999;
      if (aRank !== bRank) return aRank - bRank;
      return a.label.localeCompare(b.label);
    });
  }, [menuItems]);

  const OPERATIONS_PATHS = useMemo(
    () => ['/dashboard', '/chat', '/guests', '/rooms', '/requests', '/critical-fixes', '/bookings', '/slots', '/reminders', '/attendance', '/guest-memory', '/qr'],
    []
  );

  const operationsItems = useMemo(
    () => sidebarItems.filter((item) => OPERATIONS_PATHS.includes(item.path)),
    [sidebarItems, OPERATIONS_PATHS]
  );

  const TOPBAR_TAB_PATHS = useMemo(() => ['/requests', '/critical-fixes', '/slots', '/guests', '/department-staff'], []);

  const sidebarVisibleOperationsItems = useMemo(
    () => operationsItems.filter((item) => !TOPBAR_TAB_PATHS.includes(item.path)),
    [operationsItems, TOPBAR_TAB_PATHS]
  );

  const managementItems = useMemo(
    () => sidebarItems.filter((item) => !OPERATIONS_PATHS.includes(item.path)),
    [sidebarItems, OPERATIONS_PATHS]
  );

  useEffect(() => {
    if (OPERATIONS_PATHS.includes(location.pathname)) {
      setActiveTab('operations');
    } else {
      setActiveTab('management');
    }
  }, [location.pathname, OPERATIONS_PATHS]);

  const getPath = (path) => menuItems.find((item) => item.path === path);

  const topLeftPills = [
    getPath('/requests') ? { ...getPath('/requests'), icon: FiSend, label: 'Service Requests' } : null,
    getPath('/critical-fixes') ? { ...getPath('/critical-fixes'), icon: FiAlertCircle, label: 'Critical Fixes' } : null,
    getPath('/slots') ? { ...getPath('/slots'), icon: FiNavigation, label: 'Slot Management' } : null
  ].filter(Boolean);
  const isGuestsPage = location.pathname === '/guests';
  const isDepartmentStaffPage = location.pathname === '/department-staff';

  // Bottom nav — mobile shows only Service Requests + More
  const mobileServiceRequestItem = useMemo(() => {
    return menuItems.find((m) => m.path === '/requests') || null;
  }, [menuItems]);

  // Current page label for mobile header
  const currentPageLabel = useMemo(() => {
    const match = menuItems.find((m) => m.path === location.pathname);
    return match?.label || 'Dashboard';
  }, [menuItems, location.pathname]);

  const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';

  return (
    <div className="h-screen bg-[#f4f5fb] flex flex-col overflow-hidden">
      {/* ── Desktop: side-by-side layout ─────────────────────────────── */}
      <div className="hidden lg:flex flex-1 min-h-0 overflow-hidden">
        {/* Desktop Sidebar */}
        <aside
          className={`flex-shrink-0 bg-[#f4f5fb] flex flex-col transition-all duration-300 ease-in-out ${
            isCollapsed ? 'w-[76px]' : 'w-[248px]'
          }`}
        >
          {/* Logo + collapse */}
          <div
            className={`flex min-h-[92px] shrink-0 items-center justify-between gap-2 px-3 py-4 sm:px-4 lg:min-h-[110px] lg:items-center lg:py-0 ${
              isCollapsed ? 'lg:px-1.5' : 'lg:px-3'
            }`}
          >
            {isCollapsed ? (
              <div className="hidden h-full min-h-[100px] w-full flex-col items-center justify-center gap-2 py-3 lg:flex">
                <button
                  type="button"
                  onClick={() => setIsCollapsed(false)}
                  title="Expand sidebar"
                  aria-label="Expand sidebar"
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-white hover:text-gray-800 hover:shadow-xs cursor-pointer"
                >
                  <FiChevronsRight className="h-4 w-4" aria-hidden />
                </button>
                <div className="flex w-full items-center justify-center">
                  <Link
                    to="/"
                    className="flex items-center justify-center rounded-xl p-1 transition-transform hover:scale-110"
                    aria-label="Home"
                  >
                    <img
                      src={faviconSrc}
                      alt="Elyspr"
                      className="h-10 w-10 object-contain object-center rounded-lg"
                      onError={(e) => {
                        if (e.currentTarget.src !== brandLogoSrc) {
                          e.currentTarget.src = brandLogoSrc;
                        }
                      }}
                    />
                  </Link>
                </div>
              </div>
            ) : (
              <div className="relative hidden h-full min-h-[130px] w-full items-center justify-center px-2 py-2 lg:flex">
                <Link
                  to="/"
                  className="flex items-center justify-center w-full py-1"
                  aria-label="Home"
                >
                  <img
                    src={brandLogoSrc}
                    alt="Elyspr"
                    className="h-auto max-h-[96px] max-w-[225px] w-auto object-contain object-center transition-all duration-200 hover:scale-105"
                    onError={(e) => {
                      if (e.currentTarget.src !== 'https://elyspr.com/logo-1.png') {
                        e.currentTarget.src = 'https://elyspr.com/logo-1.png';
                      }
                    }}
                  />
                </Link>
                <button
                  type="button"
                  onClick={() => setIsCollapsed(true)}
                  title="Collapse sidebar"
                  aria-label="Collapse sidebar"
                  className="absolute right-0.5 top-1.5 z-10 flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-white hover:text-gray-800 hover:shadow-xs cursor-pointer"
                >
                  <FiChevronsLeft className="h-4 w-4" aria-hidden />
                </button>
              </div>
            )}
          </div>

          {/* Scroll area: vertically centered on desktop */}
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden">
            <div className="flex min-h-full flex-1 flex-col justify-center">
              {/* Operations / Management tube tab switcher matching topbar styling */}
              {!isCollapsed && (
                <div className="px-3 pb-3">
                  <div className="flex items-center rounded-full bg-[#eceef5]/90 p-1 shadow-xs ring-1 ring-black/[0.04]">
                    <button
                      type="button"
                      onClick={() => setActiveTab('operations')}
                      className={`flex-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${
                        activeTab === 'operations'
                          ? 'bg-gray-900 text-white shadow-sm'
                          : 'text-gray-500 hover:text-gray-900'
                      }`}
                    >
                      Operations
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('management')}
                      className={`flex-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${
                        activeTab === 'management'
                          ? 'bg-gray-900 text-white shadow-sm'
                          : 'text-gray-500 hover:text-gray-900'
                      }`}
                    >
                      Management
                    </button>
                  </div>
                </div>
              )}

              <nav
                className={`mx-auto flex w-full flex-col items-center gap-1.5 pb-6 ${
                  isCollapsed ? 'px-2' : 'px-3'
                }`}
              >
                {(activeTab === 'operations' ? sidebarVisibleOperationsItems : managementItems).map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      title={isCollapsed ? item.label : undefined}
                      className={`relative flex w-full items-center gap-2.5 rounded-xl transition-all duration-150 ${
                        isCollapsed ? 'h-11 w-11 justify-center p-0' : 'h-11 px-1'
                      } ${!isActive ? 'hover:bg-white/60' : ''}`}
                    >
                      <div
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors duration-150 ${
                          isActive
                            ? 'bg-gray-900 shadow-sm'
                            : 'bg-white shadow-sm ring-1 ring-black/[0.04]'
                        }`}
                      >
                        <Icon
                          className={`h-[19px] w-[19px] shrink-0 transition-colors duration-150 ${
                            isActive ? 'text-white' : 'text-gray-500'
                          }`}
                        />
                      </div>

                      {!isCollapsed && (
                        <span
                          className={`truncate text-[13px] font-medium transition-colors duration-150 ${
                            isActive ? 'text-gray-900 font-semibold' : 'text-gray-500'
                          }`}
                        >
                          {item.label}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </nav>
            </div>
          </div>
        </aside>

        {/* Desktop Main */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Desktop Top Bar */}
          <header
            className={`sticky top-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-300 ${
              scrolled
                ? 'border-b border-white/35 bg-white/[0.22] shadow-[0_8px_32px_rgba(0,0,0,0.06)] backdrop-blur-xl backdrop-saturate-150'
                : 'border-b border-transparent bg-transparent shadow-none backdrop-blur-none'
            }`}
          >
            <div className="flex min-h-[56px] min-w-0 items-center gap-1.5 pl-4 pr-4 py-2 sm:min-h-[68px] sm:gap-2 sm:py-2.5 lg:min-h-[100px] lg:gap-3 lg:pl-4 lg:pr-4 lg:py-5">
              {/* Left pills */}
              <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
                {topLeftPills.map((pill) => {
                  const Icon = pill.icon;
                  const isActive = location.pathname === pill.path;
                  return (
                    <button
                      key={pill.path}
                      type="button"
                      onClick={() => navigate(pill.path)}
                      className={`relative inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full px-3 py-2 text-[12px] font-medium leading-none transition-all whitespace-nowrap min-h-[38px] sm:min-h-[42px] sm:px-3.5 sm:text-[13px] lg:min-h-[44px] lg:gap-2 lg:px-4 shadow-sm ${
                        isActive
                          ? 'bg-gray-900 text-white shadow-sm'
                          : 'bg-white text-gray-600 shadow-sm ring-1 ring-black/[0.06] hover:bg-white/80'
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
                      <span>{pill.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Flex spacer pushing center */}
              <div className="hidden flex-1 lg:block" />

              {/* Center Add Guests (with 3 avatar placeholders) */}
              {getPath('/guests') && (
                <div className="hidden shrink-0 items-center gap-3 lg:flex">
                  {isAdminOrManager && (
                    <div className="flex items-center -space-x-2">
                      {[0, 1, 2].map((i) => (
                        <div
                          key={i}
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-[#f4f5fb] bg-white shadow-sm ring-1 ring-black/[0.06]"
                        >
                          <FiUsers className="h-3.5 w-3.5 text-gray-400" strokeWidth={1.75} />
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => navigate('/guests')}
                    className={`group inline-flex min-h-[38px] sm:min-h-[42px] lg:min-h-[44px] items-center justify-center gap-2 rounded-full px-4 py-2.5 text-[13px] font-medium leading-none shadow-sm transition-all whitespace-nowrap ${
                      isGuestsPage
                        ? 'bg-gray-900 text-white ring-1 ring-black/[0.08]'
                        : 'bg-white text-gray-700 ring-1 ring-black/[0.06] hover:bg-white/80 active:bg-gray-900 active:text-white active:ring-gray-900'
                    }`}
                  >
                    <FiUserPlus
                      className={`h-4 w-4 shrink-0 transition-colors ${
                        isGuestsPage ? 'text-white' : 'text-gray-500 group-active:text-white'
                      }`}
                      strokeWidth={1.75}
                    />
                    <span>{isAdminOrManager ? 'Add Guests' : 'Guests'}</span>
                  </button>
                </div>
              )}

              {/* Flex spacer pushing right */}
              <div className="hidden flex-1 lg:block" />

              {/* Right: Staff pill + Profile dropdown */}
              <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
                {isAdminOrManager && (
                  <button
                    type="button"
                    onClick={() => navigate('/department-staff')}
                    className={`relative inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full px-3 py-2 text-[12px] font-medium leading-none transition-all whitespace-nowrap min-h-[38px] sm:min-h-[42px] sm:px-3.5 sm:text-[13px] lg:min-h-[44px] lg:gap-2 lg:px-4 shadow-sm ${
                      isDepartmentStaffPage
                        ? 'bg-gray-900 text-white shadow-sm'
                        : 'bg-white text-gray-600 shadow-sm ring-1 ring-black/[0.06] hover:bg-white/80'
                    }`}
                  >
                    <FiUsers className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
                    <span>Staff</span>
                  </button>
                )}

                {/* Profile menu trigger */}
                <div className="relative z-[60] shrink-0 pl-1" ref={profileDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                    className="flex min-h-[44px] min-w-[44px] items-center gap-1.5 rounded-xl px-1.5 py-1 hover:bg-white/60 transition-colors lg:gap-2 lg:px-2"
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-gray-800 to-gray-600 text-[11px] font-bold text-white ring-2 ring-gray-100 shadow-sm lg:h-9 lg:w-9">
                      {initials}
                    </div>
                    <div className="hidden xl:flex flex-col items-start min-w-0 text-left">
                      <span className="text-[13px] font-medium text-gray-900 truncate max-w-[100px]">{firstName}</span>
                      <span className="text-[11px] text-gray-500 truncate max-w-[100px] capitalize">{user?.designation || user?.role || 'User'}</span>
                    </div>
                    <FiChevronDown
                      className={`hidden lg:block h-3.5 w-3.5 text-gray-400 transition-transform shrink-0 ${
                        profileDropdownOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {profileDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-gray-900 to-gray-700 flex items-center justify-center text-white font-medium text-sm">
                            {initials}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{user?.name || 'User'}</p>
                            <p className="text-xs text-gray-500 truncate capitalize">{user?.designation || user?.role || 'User'}</p>
                          </div>
                        </div>
                      </div>
                      {canAccessSettings && (
                        <div className="py-1">
                          <button
                            onClick={() => { setProfileDropdownOpen(false); navigate(getSettingsPath()); }}
                            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left"
                          >
                            <FiSettings className="h-4 w-4 text-gray-400" />
                            <span>Settings</span>
                          </button>
                        </div>
                      )}
                      <div className="border-t border-gray-100 pt-1">
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 text-left font-medium"
                        >
                          <FiLogOut className="h-4 w-4" />
                          <span>Sign Out</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </header>

          {/* Desktop Page Content */}
          <main
            ref={mainScrollRef}
            className={`flex-1 bg-[#f4f5fb] ${isChatPage ? 'overflow-hidden' : 'overflow-y-auto'}`}
          >
            <div className={`elychat-theme ${isChatPage ? 'h-full min-h-0' : ''}`}>
              {isChatPage ? (
                children
              ) : (
                <div className="px-1 pb-[80px] pt-0 sm:px-2 sm:pt-0.5 md:px-2.5 lg:px-2.5 lg:pb-8">
                  <div className="mx-auto w-full max-w-[1920px]">{children}</div>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>

      {/* ── Mobile Layout ─────────────────────────────────────────────── */}
      <div className="lg:hidden flex flex-col flex-1 min-h-0 overflow-hidden">
        {/* Mobile Top Bar */}
        <header className="flex-shrink-0 flex items-center justify-between h-14 px-4 bg-white border-b border-gray-200 z-30">
          <button
            onClick={() => setSidebarOpen(true)}
            className="h-9 w-9 flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50 active:bg-gray-100"
            aria-label="Open menu"
          >
            <FiMenu className="h-5 w-5 text-gray-700" />
          </button>

          <span className="text-sm font-semibold text-gray-900 truncate max-w-[180px]">{currentPageLabel}</span>

          <button
            onClick={() => setProfileDropdownOpen((p) => !p)}
            className="h-9 w-9 rounded-full bg-gradient-to-br from-gray-900 to-gray-700 flex items-center justify-center text-white font-medium text-xs ring-2 ring-gray-100 flex-shrink-0 relative"
          >
            {initials}
          </button>

          {/* Mobile profile dropdown */}
          {profileDropdownOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setProfileDropdownOpen(false)} />
              <div ref={mobileProfileDropdownRef} className="absolute right-4 top-14 w-64 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-semibold text-gray-900">{user?.name || 'User'}</p>
                  <p className="text-xs text-gray-500 capitalize">{user?.designation || user?.role || 'User'}{(!user?.designation && user?.department) ? ` · ${user.department}` : ''}</p>
                </div>
                {canAccessSettings && (
                  <div className="py-1">
                    <button
                      onClick={() => { setProfileDropdownOpen(false); navigate(getSettingsPath()); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 text-left"
                    >
                      <FiSettings className="h-4 w-4 text-gray-400" />
                      <span>Settings</span>
                    </button>
                  </div>
                )}
                <div className="border-t border-gray-100 pt-1">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 text-left"
                  >
                    <FiLogOut className="h-4 w-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </header>

        {/* Mobile Page Content */}
        <main
          ref={mainScrollRef}
          className={`flex-1 bg-[#f4f5fb] ${isChatPage ? 'overflow-hidden' : 'overflow-y-auto'}`}
          style={{ paddingBottom: isChatPage ? '0' : 'calc(64px + env(safe-area-inset-bottom))' }}
        >
          <div className={`elychat-theme ${isChatPage ? 'h-full min-h-0' : ''}`}>{children}</div>
        </main>

        {/* Mobile Bottom Navigation — Dashboard + Service Requests + More */}
        {!isChatPage && (
          <nav className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 flex items-center justify-around px-3 h-[64px]" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
            {/* Dashboard tab */}
            {(() => {
              const dashboardItem = menuItems.find((m) => m.path === '/dashboard');
              if (!dashboardItem) return null;
              const Icon = dashboardItem.icon;
              const isActive = location.pathname === '/dashboard';
              return (
                <Link
                  to="/dashboard"
                  className={`flex flex-col items-center gap-1 px-4 py-1.5 rounded-xl transition-colors min-w-0 ${
                    isActive ? 'text-gray-900' : 'text-gray-400'
                  }`}
                >
                  <div
                    className={`h-8 w-8 flex items-center justify-center rounded-full transition-colors ${
                      isActive ? 'bg-gray-900' : 'bg-gray-100'
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${isActive ? 'text-white' : 'text-gray-500'}`} />
                  </div>
                  <span className={`text-[10px] font-semibold leading-tight text-center ${
                    isActive ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    Dashboard
                  </span>
                </Link>
              );
            })()}

            {/* Service Requests tab */}
            {mobileServiceRequestItem && (() => {
              const Icon = mobileServiceRequestItem.icon;
              const isActive = location.pathname === mobileServiceRequestItem.path;
              return (
                <Link
                  to={mobileServiceRequestItem.path}
                  className={`flex flex-col items-center gap-1 px-4 py-1.5 rounded-xl transition-colors min-w-0 ${
                    isActive ? 'text-gray-900' : 'text-gray-400'
                  }`}
                >
                  <div
                    className={`h-8 w-8 flex items-center justify-center rounded-full transition-colors ${
                      isActive ? 'bg-gray-900' : 'bg-gray-100'
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${isActive ? 'text-white' : 'text-gray-500'}`} />
                  </div>
                  <span className={`text-[10px] font-semibold leading-tight text-center ${
                    isActive ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    Requests
                  </span>
                </Link>
              );
            })()}

            {/* Divider */}
            <div className="w-px h-8 bg-gray-100 flex-shrink-0" />

            {/* More — opens full sidebar */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="flex flex-col items-center gap-1 px-4 py-1.5 rounded-xl transition-colors text-gray-400 active:bg-gray-50"
            >
              <div className="h-8 w-8 flex items-center justify-center rounded-full bg-gray-100">
                <FiMoreHorizontal className="h-4 w-4 text-gray-500" />
              </div>
              <span className="text-[10px] font-semibold leading-tight text-gray-400">More</span>
            </button>
          </nav>
        )}

        {/* Mobile Sidebar Drawer */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-50 flex">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setSidebarOpen(false)}
            />

            {/* Drawer */}
            <aside className="relative w-72 max-w-[85vw] bg-white flex flex-col h-full shadow-2xl">
              {/* Drawer header */}
              <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100">
                <Link to="/" className="flex items-center justify-center flex-1 pr-2">
                  <img
                    src={brandLogoSrc}
                    alt="Elyspr"
                    className="h-auto max-h-[72px] max-w-[195px] w-auto object-contain object-center"
                    onError={(e) => {
                      if (e.currentTarget.src !== 'https://elyspr.com/logo-1.png') {
                        e.currentTarget.src = 'https://elyspr.com/logo-1.png';
                      }
                    }}
                  />
                </Link>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500"
                >
                  <FiX className="h-5 w-5" />
                </button>
              </div>

              {/* User info */}
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-gray-900 to-gray-700 flex items-center justify-center text-white font-medium text-sm flex-shrink-0">
                    {initials}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{user?.name || 'User'}</p>
                    <p className="text-xs text-gray-500 capitalize">{user?.designation || user?.role || 'User'}{(!user?.designation && user?.department) ? ` · ${user.department}` : ''}</p>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              {isAdminOrManager && (
                <div className="px-4 py-3 border-b border-gray-100 flex gap-2">
                  <button
                    onClick={() => { setSidebarOpen(false); navigate('/guests'); }}
                    className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-gray-900 text-white text-xs font-medium py-2 hover:bg-gray-800 active:bg-gray-700"
                  >
                    <FiUserPlus className="h-3.5 w-3.5" />
                    <span>Add Guest</span>
                  </button>
                  <button
                    onClick={() => { setSidebarOpen(false); navigate('/department-staff'); }}
                    className="flex-1 flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 text-gray-700 text-xs font-medium py-2 hover:bg-gray-50 active:bg-gray-100"
                  >
                    <FiUsers className="h-3.5 w-3.5" />
                    <span>Staff</span>
                  </button>
                </div>
              )}

              {/* Tabs for Admin/Manager, unified clean list for Department */}
              {isAdminOrManager && (
                <div className="px-4 py-2 border-b border-gray-100">
                  <div className="flex items-center rounded-full bg-[#eceef5]/90 p-1 shadow-xs ring-1 ring-black/[0.04]">
                    <button
                      type="button"
                      onClick={() => setActiveTab('operations')}
                      className={`flex-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${
                        activeTab === 'operations' ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-500'
                      }`}
                    >
                      Operations
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('management')}
                      className={`flex-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${
                        activeTab === 'management' ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-500'
                      }`}
                    >
                      Management
                    </button>
                  </div>
                </div>
              )}

              {/* Nav items */}
              <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
                {(isAdminOrManager ? (activeTab === 'operations' ? operationsItems : managementItems) : sidebarItems).map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-3.5 rounded-2xl px-4 py-3.5 text-sm font-medium transition-all active:scale-[0.98] ${
                        isActive
                          ? 'bg-gray-900 text-white shadow-sm'
                          : 'text-gray-700 hover:bg-gray-100 active:bg-gray-200'
                      }`}
                    >
                      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl transition-colors ${
                        isActive ? 'bg-white/15' : 'bg-gray-100'
                      }`}>
                        <Icon className={`h-4 w-4 ${isActive ? 'text-white' : 'text-gray-500'}`} />
                      </div>
                      <span className="truncate">{item.label}</span>
                      {isActive && (
                        <div className="ml-auto h-1.5 w-1.5 rounded-full bg-white/70 flex-shrink-0" />
                      )}
                    </Link>
                  );
                })}
              </nav>

              {/* Sign out */}
              <div className="px-3 pb-4 pt-2 border-t border-gray-100">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 active:bg-red-100"
                >
                  <FiLogOut className="h-4 w-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}

export default Layout;
